// Una vista por informe rápido. Todas reciben los mismos resúmenes diarios ya
// filtrados por unidad; el cálculo vive en quickReports.compute.ts y acá sólo
// se memoiza y se pinta. Las filas de cada tabla salen ya formateadas para que
// la tabla y el Excel/PDF muestren exactamente lo mismo.
import { useMemo, useState, type ReactNode } from "react";
import type { ReportTableColumn } from "../reports/components/ReportPointsTable";
import ExportButtons from "../reports/components/ExportButtons";
import { ReportStatCard } from "../reports/components/ReportCharts";
import {
  alertsReport,
  fleetSummary,
  fmtDateTime,
  fmtDay,
  fmtDuration,
  fmtKm,
  fmtKmShort,
  fmtSpeed,
  geofenceVisits,
  mileageCharts,
  mileageMatrix,
  overspeedByUnit,
  placeLabel,
  stopsByUnit,
  stopsList,
  detailOmitted,
  type UnitRef,
} from "./quickReports.compute";
import type { DailyUnitSummary } from "./types";
import BitacoraJornada from "./bitacora/BitacoraJornada";
import PagedTable, { type PagedColumn } from "./PagedTable";

export type ViewProps = {
  items: DailyUnitSummary[]; // vacío en la bitácora: pide sus propios datos
  units: UnitRef[];
  days: string[];
  tz: string;
  isDarkMode: boolean;
  periodLabel: string;
  title: string;
};

type Row = { id: string } & Record<string, string>;

const muted = (dark: boolean) => (dark ? "text-gray-400" : "text-gray-500");
const strong = (dark: boolean) => (dark ? "text-white" : "text-gray-900");
const panel = (dark: boolean) => (dark ? "bg-dark-card border-dark-border" : "bg-white border-gray-200");

const fileBase = (title: string, days: string[]) =>
  `${title.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, "-")}_${days[0]}_${days[days.length - 1]}`;

function Bar({ children, export: exp }: { children?: ReactNode; export?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4 flex-shrink-0">
      <div className="flex flex-wrap items-center gap-2 min-w-0">{children}</div>
      {exp}
    </div>
  );
}

function Exporter({ rows, columns, title, periodLabel, days }: { rows: Row[]; columns: ReportTableColumn<Row>[]; title: string; periodLabel: string; days: string[] }) {
  return <ExportButtons rows={rows} columns={columns} meta={{ title, period: periodLabel, fileBase: fileBase(title, days) }} />;
}

// Dos tablas del mismo informe: apiladas se reparten el alto del panel; en
// pantallas anchas van lado a lado (2:3).
function TwoTables({ children }: { children: ReactNode }) {
  return <div className="flex flex-col 2xl:flex-row gap-5 lg:flex-1">{children}</div>;
}

export function EmptyState({ isDarkMode, icon, title, text }: { isDarkMode: boolean; icon: string; title: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <span className="w-14 h-14 rounded-2xl bg-primary-500/15 flex items-center justify-center mb-4">
        <i className={`${icon} text-2xl text-primary-500`} aria-hidden />
      </span>
      <p className={`font-semibold ${strong(isDarkMode)}`}>{title}</p>
      <p className={`text-sm mt-1 max-w-sm ${muted(isDarkMode)}`}>{text}</p>
    </div>
  );
}

// Segmentado chico para filtros dentro de un informe (duración mínima).
export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  isDarkMode,
  label,
}: {
  value: T;
  options: { value: T; label: string; disabled?: boolean; title?: string }[];
  onChange: (v: T) => void;
  isDarkMode: boolean;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={`inline-flex p-1 rounded-xl gap-0.5 max-w-full overflow-x-auto ${isDarkMode ? "bg-white/5" : "bg-black/5"}`}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-[background-color,color,box-shadow] duration-200 ease-out disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              on
                ? `${isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900"} shadow-sm`
                : `${muted(isDarkMode)} ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------- Resumen de flota ----------

const FLEET_COLS: ReportTableColumn<Row>[] = [
  { key: "unit", header: "Unidad" },
  { key: "plate", header: "Placa" },
  { key: "km", header: "Km", align: "right" },
  { key: "driving", header: "Manejo", align: "right" },
  { key: "maneuver", header: "Maniobra", align: "right" },
  { key: "idle", header: "Ralentí", align: "right" },
  { key: "stopped", header: "Detenido", align: "right" },
  { key: "rest", header: "Descanso", align: "right" },
  { key: "noData", header: "Sin datos", align: "right" },
  { key: "stops", header: "Paradas", align: "right" },
  { key: "max", header: "Vel. máx.", align: "right" },
  { key: "over", header: "Excesos", align: "right" },
];

export function FleetView({ items, days, isDarkMode, periodLabel, title }: ViewProps) {
  const { rows, totals } = useMemo(() => fleetSummary(items), [items]);
  const table = useMemo<Row[]>(
    () =>
      rows.map((r) => ({
        id: r.id,
        unit: r.unit,
        plate: r.plate ?? "—",
        km: fmtKm(r.km),
        driving: fmtDuration(r.driving_s),
        maneuver: fmtDuration(r.maneuver_s),
        idle: fmtDuration(r.idle_s),
        stopped: fmtDuration(r.stopped_s),
        rest: fmtDuration(r.rest_s),
        noData: r.no_data_s ? fmtDuration(r.no_data_s) : "—",
        stops: String(r.stops),
        max: fmtSpeed(r.max_kmh),
        over: String(r.overspeed),
      })),
    [rows],
  );
  return (
    <>
      <div className="grid gap-3 grid-cols-2 xl:grid-cols-4 mb-5">
        <ReportStatCard isDarkMode={isDarkMode} icon="ri-road-map-line" label="Kilómetros" value={fmtKm(totals.km)} />
        <ReportStatCard isDarkMode={isDarkMode} icon="ri-steering-2-line" label="Manejo" value={fmtDuration(totals.driving_s)} />
        <ReportStatCard isDarkMode={isDarkMode} icon="ri-truck-line" label="Unidades en movimiento" value={`${totals.active} de ${totals.units}`} />
        <ReportStatCard
          isDarkMode={isDarkMode}
          icon="ri-speed-up-line"
          label="Excesos de velocidad"
          value={String(totals.overspeed)}
          iconColor={totals.overspeed ? "text-amber-500" : "text-primary-500"}
        />
      </div>
      <Bar export={<Exporter rows={table} columns={FLEET_COLS} title={title} periodLabel={periodLabel} days={days} />}>
        <span className={`text-xs ${muted(isDarkMode)}`}>
          Maniobra {fmtDuration(totals.maneuver_s)} · ralentí {fmtDuration(totals.idle_s)} · descanso {fmtDuration(totals.rest_s)}
          {totals.no_data_s ? ` · sin datos ${fmtDuration(totals.no_data_s)}` : ""} · {totals.stops} paradas · vel. máx. {fmtSpeed(totals.max_kmh)}
        </span>
      </Bar>
      <PagedTable data={table} columns={FLEET_COLS} isDarkMode={isDarkMode} emptyText="Sin unidades en el periodo" />
    </>
  );
}

// ---------- Kilometraje ----------

// Gráficas comunes en vez de la matriz tipo "contribuciones": un monitorista
// no leía intensidades de color. Barras en CSS (sin librería) para que tomen la
// escala primary-* del tema en claro y oscuro. Colores de las series apiladas:
// tres tonos del tema y gris para "Otras".
const SERIES_BG = ["bg-primary-500", "bg-primary-300", "bg-primary-700"];
const seriesBg = (i: number, id: string, dark: boolean) => (id === "others" ? (dark ? "bg-gray-500" : "bg-gray-400") : SERIES_BG[i % SERIES_BG.length]);

// Eje: sin decimales desde 100 km, para que "4,338" quepa en su columna.
const axisKm = (km: number) => (km >= 100 ? Math.round(km).toLocaleString("es-MX") : fmtKmShort(km));

function ChartCard({ title, hint, isDarkMode, children }: { title: string; hint?: ReactNode; isDarkMode: boolean; children: ReactNode }) {
  return (
    <section className={`rounded-xl border p-4 mb-5 ${panel(isDarkMode)}`} aria-label={title}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
        <h3 className={`text-sm font-semibold ${strong(isDarkMode)}`}>{title}</h3>
        {hint && <div className={`text-xs ${muted(isDarkMode)}`}>{hint}</div>}
      </div>
      {children}
    </section>
  );
}

export function MileageView({ items, days, isDarkMode, periodLabel, title }: ViewProps) {
  const m = useMemo(() => mileageMatrix(items, days), [items, days]);
  const c = useMemo(() => mileageCharts(m), [m]);
  const colLabel = (i: number) => (m.hourly ? `${m.columns[i]}:00` : fmtDay(m.columns[i]));
  // El Excel/PDF conserva la tabla unidad × hora/día, con un decimal.
  const cols = useMemo<ReportTableColumn<Row>[]>(
    () => [
      { key: "unit", header: "Unidad" },
      ...m.columns.map((col, i) => ({ key: `c${i}`, header: m.hourly ? `${col}:00` : col, align: "right" as const })),
      { key: "total", header: "Total", align: "right" },
    ],
    [m],
  );
  const table = useMemo<Row[]>(
    () => m.rows.map((r) => ({ id: r.id, unit: r.unit, total: fmtKm(r.total), ...Object.fromEntries(r.values.map((v, i) => [`c${i}`, fmtKmShort(v)])) })),
    [m],
  );
  const unitMax = c.ranked[0]?.total || 0;
  const dense = m.columns.length > 8; // 24 horas o 15 días: las etiquetas de valor no caben en móvil
  const dark = isDarkMode;
  const grid = dark ? "border-white/10" : "border-black/10";

  return (
    <>
      <div className="grid gap-3 grid-cols-2 xl:grid-cols-4 mb-5 flex-shrink-0">
        <ReportStatCard isDarkMode={dark} icon="ri-road-map-line" label="Kilómetros" value={fmtKm(m.total)} />
        <ReportStatCard isDarkMode={dark} icon="ri-truck-line" label="Unidades con recorrido" value={`${c.active} de ${m.rows.length}`} />
        <ReportStatCard
          isDarkMode={dark}
          icon="ri-trophy-line"
          label={c.active ? `Más kilómetros · ${fmtKm(c.ranked[0].total)}` : "Más kilómetros"}
          value={c.active ? c.ranked[0].unit : "—"}
        />
        <ReportStatCard
          isDarkMode={dark}
          icon="ri-time-line"
          label={`${m.hourly ? "Hora" : "Día"} con más km${c.peak >= 0 ? ` · ${fmtKm(m.colTotals[c.peak])}` : ""}`}
          value={c.peak >= 0 ? colLabel(c.peak) : "—"}
        />
      </div>
      <Bar export={<Exporter rows={table} columns={cols} title={title} periodLabel={periodLabel} days={days} />}>
        <span className={`text-xs ${muted(dark)}`}>Total {fmtKm(m.total)} en el periodo</span>
      </Bar>

      <ChartCard title="Kilómetros por unidad" hint="De mayor a menor" isDarkMode={dark}>
        <ol className="flex flex-col gap-1.5 max-h-[32rem] overflow-y-auto pr-1">
          {c.ranked.map((r) => (
            <li key={r.id} className="flex items-center gap-3 text-xs">
              <span className={`w-28 sm:w-56 flex-shrink-0 truncate ${strong(dark)}`} title={r.unit}>
                {r.unit}
              </span>
              <span className="flex-1 min-w-0 flex items-center gap-2">
                {r.total > 0 && (
                  <span
                    aria-hidden
                    className="h-5 rounded-md bg-primary-500 min-w-[3px] motion-safe:transition-[width] motion-safe:duration-200 motion-safe:ease-out"
                    style={{ width: `calc((100% - 4.5rem) * ${r.total / (unitMax || 1)})` }}
                  />
                )}
                <span className={`tabular-nums whitespace-nowrap ${r.total > 0 ? `font-semibold ${strong(dark)}` : muted(dark)}`}>{fmtKmShort(r.total)} km</span>
              </span>
            </li>
          ))}
        </ol>
      </ChartCard>

      <ChartCard
        title={m.hourly ? "Kilómetros por hora" : "Kilómetros por día"}
        isDarkMode={dark}
        hint={
          <ul className="flex flex-wrap gap-x-3 gap-y-1">
            {c.series.map((s, i) => (
              <li key={s.id} className="inline-flex items-center gap-1.5 max-w-[14rem]">
                <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${seriesBg(i, s.id, dark)}`} aria-hidden />
                <span className="truncate" title={s.name}>
                  {s.name}
                </span>
              </li>
            ))}
          </ul>
        }
      >
        <div className="relative h-56 ml-9 mt-5" role="img" aria-label={`${m.hourly ? "Kilómetros por hora" : "Kilómetros por día"}, máximo ${fmtKm(c.columnMax)}`}>
          {[1, 0.5, 0].map((f) => (
            <div key={f} aria-hidden className={`absolute inset-x-0 border-t ${f === 0 ? grid : `border-dashed ${grid}`}`} style={{ bottom: `${f * 100}%` }}>
              <span className={`absolute -left-9 -translate-y-1/2 w-8 text-right text-[10px] tabular-nums ${muted(dark)}`}>{axisKm(c.columnMax * f)}</span>
            </div>
          ))}
          <div className="absolute inset-0 flex items-end gap-[2px] sm:gap-1">
            {m.columns.map((col, i) => {
              const total = m.colTotals[i];
              const tip = [`${colLabel(i)} · ${fmtKmShort(total)} km`, ...c.series.filter((s) => s.values[i] > 0).map((s) => `${s.name}: ${fmtKmShort(s.values[i])} km`)].join("\n");
              return (
                <div key={col} className="relative flex-1 min-w-0 h-full flex flex-col justify-end" title={tip}>
                  <div
                    className="relative flex flex-col-reverse rounded-t-[4px] motion-safe:transition-[height] motion-safe:duration-200 motion-safe:ease-out"
                    style={{ height: `${(total / (c.columnMax || 1)) * 100}%` }}
                  >
                    {total > 0 && (
                      <span className={`absolute -top-4 inset-x-0 text-center text-[10px] leading-none tabular-nums font-medium ${strong(dark)} ${dense ? "max-lg:hidden" : ""}`}>
                        {fmtKmShort(total)}
                      </span>
                    )}
                    {c.series.map((s, k) =>
                      s.values[i] > 0 ? (
                        <span key={s.id} className={`block w-full last:rounded-t-[4px] ${seriesBg(k, s.id, dark)}`} style={{ height: `${(s.values[i] / total) * 100}%` }} />
                      ) : null,
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className={`flex gap-[2px] sm:gap-1 ml-9 mt-1.5 text-[10px] tabular-nums ${muted(dark)}`} aria-hidden>
          {m.columns.map((col, i) => {
            const [wd, ...rest] = m.hourly ? [] : fmtDay(col).replace(/\./g, "").replace(/,/g, "").replace(" de ", " ").split(" ");
            return (
              <span key={col} className={`flex-1 min-w-0 text-center leading-tight ${m.hourly && i % 3 ? "max-sm:invisible" : ""}`}>
                {m.hourly ? (
                  col
                ) : (
                  // 15 días en 390 px: sólo el número del día.
                  <>
                    <span className={`block uppercase opacity-70 ${dense ? "max-sm:hidden" : ""}`}>{wd}</span>
                    {rest[0]}
                    <span className={dense ? "max-sm:hidden" : ""}> {rest.slice(1).join(" ")}</span>
                  </>
                )}
              </span>
            );
          })}
        </div>
      </ChartCard>
    </>
  );
}

// ---------- Paradas ----------

// Dirección y coordenadas siempre en columnas separadas (decisión de Daniel,
// 18-oct-2026): la dirección es para leer, las coordenadas para ir. Mientras el
// backend no geocodifica una parada, su dirección es "—". El Excel/PDF lleva las
// dos columnas con el mismo texto, sin el enlace.
const STOP_COLS: PagedColumn<Row>[] = [
  { key: "unit", header: "Unidad" },
  { key: "from", header: "Desde" },
  { key: "to", header: "Hasta" },
  { key: "duration", header: "Duración", align: "right" },
  {
    key: "address",
    header: "Dirección",
    render: (r) => (
      <span className={`block max-w-[28rem] truncate ${r.address === "—" ? "opacity-50" : ""}`} title={r.address === "—" ? "Sin dirección todavía" : r.address}>
        {r.address}
      </span>
    ),
  },
  {
    key: "coords",
    header: "Coordenadas",
    render: (r) => (
      <a
        href={r.mapUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Abrir en Google Maps"
        className="inline-flex items-center gap-1 tabular-nums text-primary-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
      >
        <i className="ri-map-pin-line" aria-hidden />
        {r.coords}
      </a>
    ),
  },
  { key: "geofence", header: "Geocerca" },
];

const MIN_STOP = [
  { value: 0, label: "Todas" },
  { value: 5 * 60, label: "≥ 5 min" },
  { value: 15 * 60, label: "≥ 15 min" },
  { value: 30 * 60, label: "≥ 30 min" },
  { value: 60 * 60, label: "≥ 1 h" },
];

const STOP_UNIT_COLS: ReportTableColumn<Row>[] = [
  { key: "unit", header: "Unidad" },
  { key: "stops", header: "Paradas", align: "right" },
  { key: "stopped", header: "Detenido", align: "right" },
  { key: "rest", header: "Descanso", align: "right" },
];

export function StopsView(props: ViewProps) {
  // Varios días: el backend sólo manda totales (sin la lista de paradas).
  return detailOmitted(props.items) ? <StopsTotalsView {...props} /> : <StopsListView {...props} />;
}

function StopsTotalsView({ items, days, isDarkMode, periodLabel, title }: ViewProps) {
  const table = useMemo<Row[]>(
    () => stopsByUnit(items).map((r) => ({ id: r.id, unit: r.unit, stops: String(r.stops), stopped: fmtDuration(r.stopped_s), rest: fmtDuration(r.rest_s) })),
    [items],
  );
  return (
    <>
      <Bar export={<Exporter rows={table} columns={STOP_UNIT_COLS} title={title} periodLabel={periodLabel} days={days} />}>
        <span className={`text-xs ${muted(isDarkMode)}`}>
          Totales por unidad. El detalle de cada parada (hora, lugar, duración) se ve por día: elige Hoy o Ayer.
        </span>
      </Bar>
      <PagedTable data={table} columns={STOP_UNIT_COLS} isDarkMode={isDarkMode} emptyText="Sin paradas en el periodo" />
    </>
  );
}

function StopsListView({ items, days, tz, isDarkMode, periodLabel, title }: ViewProps) {
  const [min, setMin] = useState(5 * 60);
  const table = useMemo<Row[]>(
    () =>
      stopsList(items, min).map((s) => ({
        id: s.id,
        unit: s.unit,
        from: fmtDateTime(s.start_ts, tz),
        to: fmtDateTime(s.end_ts, tz),
        duration: fmtDuration(s.duration_s),
        address: s.place.address || "—",
        coords: `${s.place.lat.toFixed(5)}, ${s.place.lng.toFixed(5)}`,
        mapUrl: `https://www.google.com/maps?q=${s.place.lat},${s.place.lng}`,
        geofence: s.place.geofence?.name ?? "—",
      })),
    [items, min, tz],
  );
  return (
    <>
      <Bar export={<Exporter rows={table} columns={STOP_COLS} title={title} periodLabel={periodLabel} days={days} />}>
        <Segmented label="Duración mínima" value={min} options={MIN_STOP} onChange={setMin} isDarkMode={isDarkMode} />
        <span className={`text-xs ${muted(isDarkMode)}`}>{table.length} paradas</span>
      </Bar>
      <PagedTable data={table} columns={STOP_COLS} isDarkMode={isDarkMode} emptyText="Sin paradas con esa duración" />
    </>
  );
}

// ---------- Geocercas visitadas ----------

const FENCE_COLS: ReportTableColumn<Row>[] = [
  { key: "geofence", header: "Geocerca" },
  { key: "visits", header: "Visitas", align: "right" },
  { key: "units", header: "Unidades", align: "right" },
  { key: "duration", header: "Tiempo dentro", align: "right" },
];
const VISIT_COLS: ReportTableColumn<Row>[] = [
  { key: "unit", header: "Unidad" },
  { key: "geofence", header: "Geocerca" },
  { key: "enter", header: "Entrada" },
  { key: "exit", header: "Salida" },
  { key: "duration", header: "Tiempo dentro", align: "right" },
];

export function GeofencesView({ items, days, tz, isDarkMode, periodLabel, title }: ViewProps) {
  const { visits, summary } = useMemo(() => geofenceVisits(items), [items]);
  const fences = useMemo<Row[]>(
    () => summary.map((g) => ({ id: g.id, geofence: g.geofence, visits: String(g.visits), units: String(g.units), duration: fmtDuration(g.duration_s) })),
    [summary],
  );
  const list = useMemo<Row[]>(
    () =>
      visits.map((v) => ({
        id: v.id,
        unit: v.unit,
        geofence: v.geofence,
        enter: v.enter_ts == null ? "Antes del periodo" : fmtDateTime(v.enter_ts, tz),
        exit: v.exit_ts == null ? "Sigue dentro" : fmtDateTime(v.exit_ts, tz),
        duration: fmtDuration(v.duration_s),
      })),
    [visits, tz],
  );
  if (!visits.length)
    return <EmptyState isDarkMode={isDarkMode} icon="ri-map-pin-range-line" title="Sin visitas a geocercas" text="Ninguna unidad entró o salió de una geocerca en este periodo." />;
  return (
    <>
      <Bar export={<Exporter rows={list} columns={VISIT_COLS} title={title} periodLabel={periodLabel} days={days} />}>
        <span className={`text-xs ${muted(isDarkMode)}`}>
          {summary.length} geocercas · {visits.length} visitas
        </span>
      </Bar>
      <TwoTables>
        <PagedTable data={fences} columns={FENCE_COLS} isDarkMode={isDarkMode} className="2xl:flex-[2]" />
        <PagedTable data={list} columns={VISIT_COLS} isDarkMode={isDarkMode} className="2xl:flex-[3]" />
      </TwoTables>
    </>
  );
}

// ---------- Excesos de velocidad ----------

const OVER_COLS: ReportTableColumn<Row>[] = [
  { key: "unit", header: "Unidad" },
  { key: "count", header: "Excesos", align: "right" },
  { key: "max", header: "Vel. máx.", align: "right" },
  { key: "duration", header: "Tiempo en exceso", align: "right" },
  { key: "limit", header: "Límite", align: "right" },
];

export function OverspeedView({ items, days, isDarkMode, periodLabel, title }: ViewProps) {
  const rows = useMemo(() => overspeedByUnit(items), [items]);
  const table = useMemo<Row[]>(
    () =>
      rows
        .filter((r) => r.count > 0)
        .map((r) => ({ id: r.id, unit: r.unit, count: String(r.count), max: fmtSpeed(r.max_kmh), duration: fmtDuration(r.duration_s), limit: r.limit_kmh ? fmtSpeed(r.limit_kmh) : "—" })),
    [rows],
  );
  const total = rows.reduce((a, r) => a + r.count, 0);
  if (!total)
    return <EmptyState isDarkMode={isDarkMode} icon="ri-speed-up-line" title="Sin excesos de velocidad" text="Ninguna unidad rebasó el límite en este periodo." />;
  const top = rows[0];
  return (
    <>
      <div className="grid gap-3 grid-cols-2 xl:grid-cols-3 mb-5">
        <ReportStatCard isDarkMode={isDarkMode} icon="ri-speed-up-line" iconColor="text-amber-500" label="Excesos" value={String(total)} />
        <ReportStatCard isDarkMode={isDarkMode} icon="ri-truck-line" label="Unidades con excesos" value={`${table.length} de ${rows.length}`} />
        <ReportStatCard isDarkMode={isDarkMode} icon="ri-alarm-warning-line" iconColor="text-amber-500" label="Más excesos" value={`${top.unit} (${top.count})`} />
      </div>
      <Bar export={<Exporter rows={table} columns={OVER_COLS} title={title} periodLabel={periodLabel} days={days} />} />
      <PagedTable data={table} columns={OVER_COLS} isDarkMode={isDarkMode} />
    </>
  );
}

// ---------- Alertas ----------

const ALERT_LABEL = { sos: "SOS", power: "Energía desconectada", gps: "Pérdida de señal" } as const;
const ALERT_ICON = { sos: "ri-alarm-warning-line", power: "ri-plug-line", gps: "ri-signal-wifi-off-line" } as const;

const ALERT_UNIT_COLS: ReportTableColumn<Row>[] = [
  { key: "unit", header: "Unidad" },
  { key: "sos", header: "SOS", align: "right" },
  { key: "power", header: "Energía desconectada", align: "right" },
  { key: "gps", header: "Pérdida de señal", align: "right" },
  { key: "total", header: "Total", align: "right" },
];
const ALERT_LIST_COLS: ReportTableColumn<Row>[] = [
  { key: "when", header: "Fecha y hora" },
  { key: "unit", header: "Unidad" },
  { key: "type", header: "Alerta" },
  { key: "place", header: "Lugar" },
];

export function AlertsView({ items, days, tz, isDarkMode, periodLabel, title }: ViewProps) {
  const r = useMemo(() => alertsReport(items), [items]);
  const byUnit = useMemo<Row[]>(
    () => r.byUnit.map((u) => ({ id: u.id, unit: u.unit, sos: String(u.sos), power: String(u.power), gps: String(u.gps), total: String(u.total) })),
    [r],
  );
  const list = useMemo<Row[]>(
    () => r.list.map((a) => ({ id: a.id, when: fmtDateTime(a.ts, tz), unit: a.unit, type: ALERT_LABEL[a.kind], place: placeLabel(a.place) })),
    [r, tz],
  );
  if (!r.list.length)
    return <EmptyState isDarkMode={isDarkMode} icon="ri-shield-check-line" title="Sin alertas del equipo" text="No hubo SOS, desconexiones de energía ni pérdidas de señal en este periodo." />;
  return (
    <>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-3 mb-5">
        {(Object.keys(ALERT_LABEL) as (keyof typeof ALERT_LABEL)[]).map((k) => (
          <ReportStatCard
            key={k}
            isDarkMode={isDarkMode}
            icon={ALERT_ICON[k]}
            iconColor={r.totals[k] ? (k === "sos" ? "text-red-500" : "text-amber-500") : "text-primary-500"}
            label={ALERT_LABEL[k]}
            value={String(r.totals[k])}
          />
        ))}
      </div>
      <Bar export={<Exporter rows={list} columns={ALERT_LIST_COLS} title={title} periodLabel={periodLabel} days={days} />} />
      <TwoTables>
        <PagedTable data={byUnit} columns={ALERT_UNIT_COLS} isDarkMode={isDarkMode} className="2xl:flex-[2]" />
        <PagedTable data={list} columns={ALERT_LIST_COLS} isDarkMode={isDarkMode} className="2xl:flex-[3]" />
      </TwoTables>
    </>
  );
}

// ---------- Bitácora de jornada ----------

// Una unidad y un día; la bitácora trae su propio selector de fecha (abre en el
// último día del periodo elegido arriba) y sus propias descargas.
export function BitacoraView({ units, days, isDarkMode }: ViewProps) {
  const [picked, setPicked] = useState("");
  const unit = units.find((u) => u.id === picked) ?? units[0];
  if (!unit) return null;
  return (
    <div className="max-w-2xl xl:max-w-[62rem]">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <label className={`text-xs ${muted(isDarkMode)}`} htmlFor="qr-bitacora-unit">
          Unidad
        </label>
        <select
          id="qr-bitacora-unit"
          value={unit.id}
          onChange={(e) => setPicked(e.target.value)}
          className={`h-9 rounded-md border px-3 text-sm outline-none focus:ring-2 focus:ring-primary-500 ${
            isDarkMode ? "bg-dark-surface border-dark-border text-white" : "bg-white border-gray-300 text-gray-900"
          }`}
        >
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
              {u.plate ? ` · ${u.plate}` : ""}
            </option>
          ))}
        </select>
      </div>
      <BitacoraJornada key={unit.id} deviceId={unit.id} unitName={unit.name} isDarkMode={isDarkMode} initialDate={days[days.length - 1]} hos />
    </div>
  );
}
